import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Pencil, Calendar, Save, X, Users } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Input } from '@/shared/components/ui/Input';
import { Card } from '@/shared/components/ui/Card';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useLeaveTypes,
  useLeaveTypeMutations,
  useHolidays,
  useHolidayMutations,
  useApplyLeavePolicy,
} from '../hooks/useHr';
import { fmtDate } from '../constants';
import type { LeaveTypeConfig, CreateLeaveTypePayload } from '../types';

// ─── Leave Types Tab ──────────────────────────────────────────────────────────

/** The leave policy: each type, its yearly quota and rules. Shown under Settings > HR > Leave Types. */
export function LeaveTypesPanel() {
  const { data: leaveTypes = [], isLoading } = useLeaveTypes(true);
  const { createLeaveType, updateLeaveType, deleteLeaveType } = useLeaveTypeMutations();
  const [showCreate, setShowCreate] = useState(false);
  const [editCode, setEditCode] = useState<string | null>(null);

  if (isLoading) return <Spinner />;

  return (
    <div className="flex flex-col gap-4">
      <ApplyPolicyCard />

      <div className="flex items-center justify-between">
        <p className="text-body-sm text-on-surface-variant">
          {leaveTypes.length} leave type{leaveTypes.length !== 1 ? 's' : ''} configured
        </p>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> Add Type
        </Button>
      </div>

      {showCreate && (
        <LeaveTypeForm
          onSubmit={(payload) => {
            createLeaveType.mutate(payload, {
              onSuccess: () => { toast.success('Leave type created.'); setShowCreate(false); },
              onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to create leave type.')),
            });
          }}
          onCancel={() => setShowCreate(false)}
          loading={createLeaveType.isPending}
        />
      )}

      {leaveTypes.length === 0 && !showCreate && (
        <EmptyState title="No leave types" description="Create your first leave type to get started." />
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {leaveTypes.map((lt) =>
          editCode === lt.code ? (
            <LeaveTypeForm
              key={lt.code}
              initial={lt}
              onSubmit={(payload) => {
                updateLeaveType.mutate(
                  { code: lt.code, payload },
                  {
                    onSuccess: () => { toast.success('Leave type updated.'); setEditCode(null); },
                    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to update.')),
                  },
                );
              }}
              onCancel={() => setEditCode(null)}
              loading={updateLeaveType.isPending}
              isEdit
            />
          ) : (
            <LeaveTypeCard
              key={lt.code}
              lt={lt}
              onEdit={() => setEditCode(lt.code)}
              onDelete={() => {
                if (!confirm(`Deactivate "${lt.name}"? Employees won't be able to select it.`)) return;
                deleteLeaveType.mutate(lt.code, {
                  onSuccess: () => toast.success('Leave type deactivated.'),
                  onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to deactivate.')),
                });
              }}
            />
          ),
        )}
      </div>
    </div>
  );
}

/** Make every employee's balance match the policy. Balances are also created automatically, so this is for after a change. */
function ApplyPolicyCard() {
  const apply = useApplyLeavePolicy();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [includeManual, setIncludeManual] = useState(false);

  const run = (mode: 'fill' | 'reapply') => {
    if (mode === 'reapply' && !confirm(`Bring ${year} balances in line with the policy now?\n\nBalances that came from the policy are changed to the new quota (never below what is already used or waiting for approval).${includeManual ? '\n\nBalances HR typed by hand will be changed too.' : '\n\nBalances HR typed by hand are left alone.'}`)) return;
    apply.mutate(
      { year, mode, include_manual: includeManual },
      {
        onSuccess: (r) =>
          toast.success(
            mode === 'fill'
              ? `${r.created} balance${r.created !== 1 ? 's' : ''} added for ${r.people} people.`
              : `${r.updated} balance${r.updated !== 1 ? 's' : ''} updated, ${r.created} added, ${r.kept} already right.`
          ),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not apply the policy.')),
      }
    );
  };

  return (
    <Card className="flex flex-col gap-3 px-4 py-3">
      <div className="flex items-start gap-3">
        <Users className="mt-0.5 h-5 w-5 text-primary" />
        <div>
          <p className="font-medium text-on-surface">Every employee&apos;s leave comes from this policy</p>
          <p className="text-body-sm text-on-surface-variant">
            Each year, and when someone joins, everyone gets the quota below for each leave type. Someone who joins part-way through the year gets a share of
            the types marked pro-rated. Changing a quota does not change balances that already exist: use the buttons here when you want it to.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-28">
          <Select
            label="Year"
            name="policy-year"
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
            options={[thisYear, thisYear + 1].map((y) => ({ value: String(y), label: String(y) }))}
          />
        </div>
        <Button size="sm" variant="outline" loading={apply.isPending} onClick={() => run('fill')}>
          Give everyone their balance
        </Button>
        <Button size="sm" variant="outline" loading={apply.isPending} onClick={() => run('reapply')}>
          Re-apply policy to existing balances
        </Button>
        <label className="flex cursor-pointer items-center gap-2 text-body-sm text-on-surface">
          <input type="checkbox" checked={includeManual} onChange={(e) => setIncludeManual(e.target.checked)} className="h-4 w-4 accent-primary" />
          Also change balances HR typed by hand
        </label>
      </div>
    </Card>
  );
}

function LeaveTypeCard({ lt, onEdit, onDelete }: { lt: LeaveTypeConfig; onEdit: () => void; onDelete: () => void }) {
  return (
    <Card className="relative overflow-hidden px-4 py-3">
      <div className="absolute left-0 top-0 h-full w-1.5" style={{ backgroundColor: lt.color }} />
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-on-surface">{lt.name}</p>
          <p className="text-[10px] uppercase text-on-surface-variant">{lt.code}</p>
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={onEdit} className="rounded p-1 hover:bg-surface-variant" aria-label="Edit">
            <Pencil className="h-3.5 w-3.5 text-on-surface-variant" />
          </button>
          {lt.is_active && (
            <button type="button" onClick={onDelete} className="rounded p-1 hover:bg-surface-variant" aria-label="Deactivate">
              <Trash2 className="h-3.5 w-3.5 text-danger" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Badge tone={lt.is_paid ? 'success' : 'neutral'}>{lt.is_paid ? 'Paid' : 'Unpaid'}</Badge>
        <Badge tone="info">{lt.default_annual_quota}d/yr</Badge>
        {lt.prorate && <Badge tone="info">Pro-rated</Badge>}
        {!lt.is_active && <Badge tone="danger">Inactive</Badge>}
        {lt.requires_document && <Badge tone="warning">Doc Required</Badge>}
      </div>
    </Card>
  );
}

function LeaveTypeForm({
  initial,
  onSubmit,
  onCancel,
  loading,
  isEdit,
}: {
  initial?: LeaveTypeConfig;
  onSubmit: (payload: CreateLeaveTypePayload) => void;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [isPaid, setIsPaid] = useState(initial?.is_paid ?? true);
  const [quota, setQuota] = useState(String(initial?.default_annual_quota ?? 0));
  const [requiresDoc, setRequiresDoc] = useState(initial?.requires_document ?? false);
  const [docThreshold, setDocThreshold] = useState(String(initial?.document_threshold_days ?? 3));
  const [color, setColor] = useState(initial?.color ?? '#3b82f6');
  const [prorate, setProrate] = useState(initial?.prorate ?? false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      code: code.toLowerCase().trim(),
      name: name.trim(),
      is_paid: isPaid,
      default_annual_quota: Number(quota) || 0,
      requires_document: requiresDoc,
      document_threshold_days: Number(docThreshold) || 0,
      prorate,
      color,
    });
  };

  return (
    <Card className="px-4 py-3">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Code"
            name="lt-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. casual"
            disabled={isEdit}
            required
          />
          <Input
            label="Name"
            name="lt-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Casual Leave"
            required
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-on-surface">Paid?</label>
            <Select
              name="lt-paid"
              value={isPaid ? 'yes' : 'no'}
              onChange={(e) => setIsPaid(e.target.value === 'yes')}
              options={[{ value: 'yes', label: 'Paid' }, { value: 'no', label: 'Unpaid' }]}
            />
          </div>
          <Input
            label="Annual Quota"
            name="lt-quota"
            type="number"
            value={quota}
            onChange={(e) => setQuota(e.target.value)}
            min="0"
          />
          <div>
            <label className="mb-1 block text-[11px] font-medium text-on-surface">Color</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 w-full cursor-pointer rounded-DEFAULT border border-outline"
            />
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={prorate}
            onChange={(e) => setProrate(e.target.checked)}
            className="h-4 w-4 rounded border border-outline bg-white text-primary accent-primary focus:ring-2 focus:ring-primary/40"
          />
          <span className="text-body-sm text-on-surface">Pro-rate for people who join part-way through the year</span>
        </label>
        <div className="flex items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={requiresDoc}
              onChange={(e) => setRequiresDoc(e.target.checked)}
              className="h-4 w-4 rounded border border-outline bg-white text-primary accent-primary focus:ring-2 focus:ring-primary/40"
            />
            <span className="text-body-sm text-on-surface">Requires document</span>
          </label>
          {requiresDoc && (
            <Input
              label=""
              name="lt-doc-threshold"
              type="number"
              value={docThreshold}
              onChange={(e) => setDocThreshold(e.target.value)}
              min="1"
              placeholder="Days threshold"
              className="w-24"
            />
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button type="submit" size="sm" loading={loading}>
            <Save className="h-3.5 w-3.5" /> {isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ─── Holidays Tab ─────────────────────────────────────────────────────────────

interface HolidayRow {
  id: string;
  date: string;
  name: string;
  is_optional: boolean;
}

let rowIdCounter = 0;
function newRow(): HolidayRow {
  return { id: `row-${++rowIdCounter}`, date: '', name: '', is_optional: false };
}

/** The holiday calendar: compulsory and optional holidays, and how many optional ones each person may take. */
export function HolidaysPanel() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { data: holidays = [], isLoading } = useHolidays(year);
  const { createHoliday, deleteHoliday } = useHolidayMutations();
  const [rows, setRows] = useState<HolidayRow[]>([]);
  const [saving, setSaving] = useState(false);

  const yearOptions = useMemo(() => {
    return [currentYear - 1, currentYear, currentYear + 1].map((y) => ({
      value: String(y),
      label: String(y),
    }));
  }, [currentYear]);

  const addRow = () => setRows((prev) => [...prev, newRow()]);

  const updateRow = (id: string, field: keyof HolidayRow, value: string | boolean) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSaveAll = async () => {
    const valid = rows.filter((r) => r.date && r.name.trim());
    if (valid.length === 0) return;
    setSaving(true);
    let success = 0;
    for (const row of valid) {
      try {
        await createHoliday.mutateAsync({ date: row.date, name: row.name.trim(), is_optional: row.is_optional });
        success++;
      } catch {
        // skip duplicates silently
      }
    }
    setSaving(false);
    if (success > 0) {
      toast.success(`${success} holiday${success > 1 ? 's' : ''} added.`);
      setRows([]);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select
            name="holiday-year"
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
            options={yearOptions}
          />
          <p className="text-body-sm text-on-surface-variant">
            {holidays.length} holiday{holidays.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button size="sm" onClick={addRow}>
          <Plus className="h-4 w-4" /> Add Holiday
        </Button>
      </div>

      <OptionalHolidayAllowance />

      {/* Multi-row add form */}
      {rows.length > 0 && (
        <Card className="px-4 py-3">
          <div className="flex flex-col gap-2">
            {/* Header row */}
            <div className="grid grid-cols-[140px_1fr_80px_32px] items-center gap-3 text-[11px] font-medium uppercase text-on-surface-variant">
              <span>Date</span>
              <span>Name</span>
              <span>Optional</span>
              <span />
            </div>
            {/* Data rows */}
            {rows.map((row) => (
              <div key={row.id} className="grid grid-cols-[140px_1fr_80px_32px] items-center gap-3">
                <div className="relative">
                  <input
                    type="date"
                    value={row.date}
                    onChange={(e) => updateRow(row.id, 'date', e.target.value)}
                    className="h-9 w-full rounded-DEFAULT border border-outline bg-white px-2 pr-8 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <Calendar className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
                </div>
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => updateRow(row.id, 'name', e.target.value)}
                  placeholder="e.g. Republic Day"
                  className="h-9 rounded-DEFAULT border border-outline bg-white px-3 text-data-md text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <label className="flex cursor-pointer items-center justify-center">
                  <input
                    type="checkbox"
                    checked={row.is_optional}
                    onChange={(e) => updateRow(row.id, 'is_optional', e.target.checked)}
                    className="h-4 w-4 cursor-pointer appearance-none rounded-none border border-black bg-white checked:border-black checked:bg-primary focus:ring-2 focus:ring-primary/40"
                    style={{ backgroundImage: row.is_optional ? "url(\"data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")" : undefined, backgroundSize: '100% 100%', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="flex h-8 w-8 items-center justify-center rounded hover:bg-surface-variant"
                  aria-label="Remove row"
                >
                  <X className="h-3.5 w-3.5 text-danger" />
                </button>
              </div>
            ))}
          </div>
          {/* Footer actions */}
          <div className="mt-3 flex items-center justify-between">
            <Button type="button" variant="ghost" size="sm" onClick={addRow}>
              <Plus className="h-3.5 w-3.5" /> Add Another Row
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setRows([])}>
                Cancel
              </Button>
              <Button
                size="sm"
                loading={saving}
                onClick={handleSaveAll}
                disabled={rows.every((r) => !r.date || !r.name.trim())}
              >
                <Save className="h-3.5 w-3.5" /> Save {rows.filter((r) => r.date && r.name.trim()).length} Holiday{rows.filter((r) => r.date && r.name.trim()).length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {holidays.length === 0 && rows.length === 0 && (
        <EmptyState title="No holidays" description={`No holidays configured for ${year}.`} />
      )}

      <div className="flex flex-col gap-2">
        {holidays.map((h) => (
          <Card key={h.id} className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-primary" />
              <div>
                <p className="font-medium text-on-surface">{h.name}</p>
                <p className="text-[11px] text-on-surface-variant">{fmtDate(h.date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge tone={h.is_optional ? 'info' : 'neutral'}>{h.is_optional ? 'Optional' : 'Mandatory'}</Badge>
              <button
                type="button"
                onClick={() => {
                  if (!confirm(`Remove "${h.name}" from the holiday calendar?`)) return;
                  deleteHoliday.mutate(h.id, {
                    onSuccess: () => toast.success('Holiday removed.'),
                    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to remove holiday.')),
                  });
                }}
                className="rounded p-1 hover:bg-surface-variant"
                aria-label="Remove"
              >
                <Trash2 className="h-3.5 w-3.5 text-danger" />
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}


/** How many optional holidays each person may take a year. It is the leave type 'optional', so it shows up in balances like any other. */
function OptionalHolidayAllowance() {
  const { data: types = [] } = useLeaveTypes(true);
  const { createLeaveType, updateLeaveType } = useLeaveTypeMutations();
  const existing = types.find((t) => t.code === 'optional');
  const current = existing && existing.is_active ? existing.default_annual_quota : 0;
  const [value, setValue] = useState<string | null>(null);
  const shown = value ?? String(current);
  const n = Math.max(0, Math.floor(Number(shown) || 0));
  const changed = value !== null && n !== current;

  const save = () => {
    const done = () => {
      toast.success(n === 0 ? 'Optional holidays switched off.' : `Each person may take ${n} optional holiday${n !== 1 ? 's' : ''} a year.`);
      setValue(null);
    };
    const fail = (err: unknown) => toast.error(getApiErrorMessage(err, 'Could not save.'));
    if (existing) updateLeaveType.mutate({ code: 'optional', payload: { default_annual_quota: n, is_active: n > 0 } }, { onSuccess: done, onError: fail });
    else if (n > 0) createLeaveType.mutate({ code: 'optional', name: 'Optional Holiday', is_paid: true, default_annual_quota: n, color: '#a855f7' }, { onSuccess: done, onError: fail });
  };

  return (
    <Card className="flex flex-wrap items-end justify-between gap-3 px-4 py-3">
      <div className="max-w-xl">
        <p className="font-medium text-on-surface">Optional holidays each person may take a year</p>
        <p className="text-body-sm text-on-surface-variant">
          Holidays marked Optional below are not days off for everyone. Each employee may choose this many of them (they apply under Leave, type &quot;Optional Holiday&quot;). Set 0 to switch it off.
        </p>
      </div>
      <div className="flex items-end gap-2">
        <div className="w-24">
          <Input label="Per year" name="optional-quota" type="number" min="0" value={shown} onChange={(e) => setValue(e.target.value)} />
        </div>
        <Button size="sm" disabled={!changed} loading={createLeaveType.isPending || updateLeaveType.isPending} onClick={save}>
          <Save className="h-3.5 w-3.5" /> Save
        </Button>
      </div>
    </Card>
  );
}
